import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/helpers/utils';
import { Dialog, Transition } from '@headlessui/react';
import { navigate } from 'gatsby';
import { CirclePlus, TriangleAlert, X } from 'lucide-react';
import React, { Fragment } from 'react';
import { useAppSelector } from '../../app/redux/hooks';

type Props = {
  closeModal: () => void;
  children: React.ReactNode;
};

export const ModalContent = ({ closeModal, children }: Props) => {
  const { isCloseDisabled, modalConfig } = useAppSelector((state) => state.globalModal);

  return (
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
    >
      <Dialog.Panel
        className={cn(
          '@container/dialog max-h-[calc(100svh_-_2rem)] md:max-h-[720px] relative grid rounded-xl gap-4 md:rounded-3xl bg-white text-left xl:p-10 md:p-6 p-4 overflow-hidden [&:has(.dialog-header)]:grid-rows-[auto_1fr] [&:has(.dialog-footer)]:grid-rows-[auto_1fr]',
          modalConfig?.minheightContent ? modalConfig.minheightContent : 'h-content',
          modalConfig?.minWidthContent ? modalConfig.minWidthContent : 'w-screen',
          !modalConfig?.fullScreen && 'shadow',
          modalConfig?.dialogClasses
        )}
      >
        <>
          {!isCloseDisabled && (
            <button
              type="button"
              className={`absolute ${
                modalConfig?.fullScreen && 'md:hidden block'
              } right-4 top-4 md:right-6 md:top-6 rounded-full opacity-20 transition-opacity hover:opacity-50 disabled:pointer-events-none`}
              onClick={closeModal}
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5 @lg/dialog:h-6 @lg/dialog:w-6 stroke-2" aria-hidden="true" />
            </button>
          )}
          {modalConfig?.icon && (
            <div className="dialog-header">
              {modalConfig?.icon === 'plus' ? (
                <CirclePlus
                  className="bg-primary-100 flex h-[52px] items-center justify-center mx-auto rounded-full text-primary w-15"
                  aria-hidden="true"
                />
              ) : (
                <TriangleAlert
                  className="flex h-[52px] items-center justify-center mx-auto text-danger w-15"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
          {children && (
            <div
              className={cn(
                'overflow-auto pretty-scroll grid gap-4 [&:has(.dialog-content-header)]:grid-rows-[auto_1fr] [&:has(.dialog-content-footer)]:grid-rows-[auto_1fr] [&:has(.dialog-content-header)]:overflow-hidden [&:has(.dialog-content-footer)]:overflow-hidden',
                modalConfig?.contentClasses
              )}
            >
              {children}
            </div>
          )}
          {modalConfig?.actions && modalConfig?.actions.length > 0 && (
            <div className="dialog-footer flex space-x-3">
              {modalConfig?.actions.map((action: any, i: number) => {
                return (
                  <Button
                    key={i}
                    disabled={isCloseDisabled}
                    appearance={action.appearance ?? 'gray'}
                    variant={action.variant ?? 'default'}
                    className={action.fullWidth ? 'w-full' : ''}
                    size={action.size ?? 'md'}
                    onClick={() =>
                      action.action
                        ? action.action === 'close'
                          ? closeModal()
                          : action.action === 'link'
                            ? navigate(action.url)
                            : null
                        : null
                    }
                  >
                    {action.text}
                  </Button>
                );
              })}
            </div>
          )}
        </>
      </Dialog.Panel>
    </Transition.Child>
  );
};
